import React from 'react';
import styles from './ChatroomAddMembers.scss';
import { MemberList } from '../ChatCreation/MemberList/MemberList';
import {
  withMemberSearch,
  WithMemberSearchInjectedProps,
} from '../../hoc/withMemberSearch/withMemberSearch';
import { connect } from 'react-redux';
import {
  headerFontFamily,
  messagesFontFamily,
  primaryColorOrDefault,
  textColorOrDefault,
} from '../../reducers';
import utils from '../../services/utils';
import { InjectedTranslateProps, translate } from 'react-i18next';
import { SearchBox } from '../SearchBox/SearchBox';
import SelectedMembersList from '../ChatCreation/SelectedMembersList/SelectedMembersList';
import { NoMembersFound } from '../ChatCreation/NoMembersFound/NoMembersFound';
import { NoInitialMembers } from '../ChatCreation/NoInitialMembers/NoInitialMembers';
import Button from '../Button/Button';
import {
  CheckboxCheck,
  CheckboxUncheck,
  SiteMember,
} from '../ChatCreation/ChatCreation';
import { Loader } from '../Loader/Loader';

interface ChatroomAddMembersProps
  extends WithMemberSearchInjectedProps,
    InjectedTranslateProps {
  primaryColor: string;
  textColor: string;
  onChangeHeader: Function;
  onAdd(userIds: string[]): any;
  participants?: any[];
  messageFont: string;
  headerFont: string;
}

interface ChatroomAddMembersState {
  selectedMembers: SiteMember[];
}

class _ChatroomAddMembers extends React.Component<
  ChatroomAddMembersProps,
  ChatroomAddMembersState
> {
  readonly state = {
    selectedMembers: [] as SiteMember[],
  };

  handleSelect = async (userId) => {
    const userIdAlreadySelected: boolean = !!this.state.selectedMembers.find(
      (member) => member.userId === userId,
    );
    const selectedMember: SiteMember | undefined = this.props.members.find(
      (member) => member.userId === userId,
    );

    const afterStateChange = () => {
      if (this.props.onChangeHeader) {
        this.props.onChangeHeader(this.state.selectedMembers.length);
      }
    };

    if (!userIdAlreadySelected) {
      if (selectedMember) {
        this.setState(
          {
            selectedMembers: [...this.state.selectedMembers, selectedMember],
          },
          afterStateChange,
        );
      }
    } else {
      this.setState(
        {
          selectedMembers: this.state.selectedMembers.filter(
            (member) => member.userId !== userId,
          ),
        },
        afterStateChange,
      );
    }
  };

  handleAddButton = async () => {
    const { onAdd } = this.props;

    const selectedUserIds = this.state.selectedMembers.map(
      (member) => member.userId,
    );
    onAdd(selectedUserIds);
  };

  render() {
    const {
      t,
      members,
      onFetchMore,
      searchTerm,
      onMemberSearch,
      loadingMembers,
      primaryColor,
      textColor,
      participants,
      messageFont,
      headerFont,
    } = this.props;

    const participantIds = (participants || []).map(
      (participant) => participant.id,
    );
    const membersToDisplay = members.filter(
      (member) => participantIds.indexOf(member.userId) === -1,
    );

    const hasMembers = membersToDisplay.length > 0;
    const hasSelectedMembers = this.state.selectedMembers.length > 0;

    return (
      <div
        className={styles.chatroomAddMembers}
        style={{ fontFamily: messageFont }}
      >
        {(hasMembers || searchTerm) && (
          <SearchBox
            onChange={onMemberSearch}
            placeholder={t('social.new-chat.search-placeholder')}
          />
        )}
        {hasSelectedMembers && (
          <div className={styles.selectedMembers}>
            <SelectedMembersList
              numberOfMemberToScroll={6}
              numberOfMemberToPresent={6}
              selectedMembers={this.state.selectedMembers}
              onClickOnMember={this.handleSelect}
            />
          </div>
        )}
        <div className={styles.memberList}>
          {loadingMembers ? (
            <div className={styles.loaderWrapper}>
              <Loader />
            </div>
          ) : hasMembers ? (
            <MemberList
              members={membersToDisplay}
              onSelect={this.handleSelect}
              onFetchMore={onFetchMore}
              entrySuffix={(id: string) => {
                const isSelected = !!this.state.selectedMembers.find(
                  (member) => member.userId === id,
                );

                return (
                  <div
                    className={styles.checkbox}
                    data-hook="group-chat-check-box"
                  >
                    {isSelected ? (
                      <CheckboxCheck
                        size="20px"
                        data-hook="group-chat-check-box-check"
                        primarycolor={primaryColor}
                      />
                    ) : (
                      <CheckboxUncheck
                        size="20px"
                        data-hook="group-chat-check-box-uncheck"
                        primarycolor={primaryColor}
                      />
                    )}
                  </div>
                );
              }}
            />
          ) : searchTerm ? (
            <NoMembersFound searchTerm={searchTerm} />
          ) : (
            <NoInitialMembers />
          )}
          {hasSelectedMembers && (
            <div
              className={styles.addButton}
              style={{ fontFamily: headerFont }}
            >
              <Button dataHook="add-button" onClick={this.handleAddButton}>
                {t('chatroom-add-members.add-button.label')}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  primaryColor: primaryColorOrDefault(state),
  textColor: textColorOrDefault(state),
  messageFont: messagesFontFamily(state),
  headerFont: headerFontFamily(state),
});

const mapDispatchToProps = (state) => ({});

export const ChatroomAddMembers = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withMemberSearch(translate()(_ChatroomAddMembers)));
